import React, {lazy, Suspense, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Container, Nav, Navbar, NavDropdown} from "react-bootstrap";
import {IconButton, Tooltip} from "@mui/material";
import {useBrandLogo} from '../utilities/genreIcons';
import {AdminPanelSettings, BugReport, ChevronLeft, Forum, CreditCard, Fullscreen, FullscreenExit, HelpOutline, Info, Logout, ManageAccounts, QuestionMark, Storage as StorageIcon} from "@mui/icons-material";
import {useFeatureFlags} from "../context/FeatureFlagContext";
import {FaDiscord, FaInstagram} from "react-icons/fa";
import ProfileModal from "./ProfileModal";
import GlobalSettingsModal from "./GlobalSettingsModal";
import FeedbackModal from "./FeedbackModal";
import {Settings} from "@mui/icons-material/";
import {announceLogout} from "../utilities/WebSocketProvider";
import {useLightMode} from "../context/ThemeContext";
import {useHelp} from "../context/HelpContext";
import {clearSageHistory, confirmLogout} from "../utilities/Utils";
import {DEFAULT_PROFILE_AVATAR, profileAvatarUrl} from "../utilities/profileAvatar";
import Swal from "sweetalert2";


// About carries the licence texts (OGL, ORC, SRD attribution), so it is fetched the first time it
// is opened rather than with every page; once loaded it stays mounted, so it still animates closed.
const AboutModal = lazy(() => import("./AboutModal"));
// The Help Guide is loaded the same way: its topics, icons and screenshots are only needed once
// someone opens it.
const HelpModal = lazy(() => import("./HelpModal"));

const Header = () => {
    let userJSON = JSON.parse(sessionStorage.getItem('user'));
    const {lightMode, showTooltips} = useLightMode();
    const DDLogo = useBrandLogo();
    const {helpModeOn, toggleHelp} = useHelp();
    const navigate = useNavigate();
    const {superAdmin} = useFeatureFlags();
    // Starts on the bundled default so a new account - or one whose profile hasn't loaded yet -
    // shows an avatar rather than an empty circle; an upload replaces it once the profile lands.
    const [avatarImage, setAvatarImage] = useState(DEFAULT_PROFILE_AVATAR);
    const [campaignName, setCampaignName] = useState('');
    const [communityOpen, setCommunityOpen] = useState(false);
    const [partyName, setPartyName] = useState('');
    const [avatarBorderColor, setAvatarBorderColor] = useState('');
    const [showProfile, setShowProfile] = useState(false);
    const handleShowProfile = () => setShowProfile(true);
    const [showHelp, setShowHelp] = useState(false);
    const [helpLoaded, setHelpLoaded] = useState(false);
    const openHelp = () => { setHelpLoaded(true); setShowHelp(true); };
    const [showAbout, setShowAbout] = useState(false);
    const [aboutLoaded, setAboutLoaded] = useState(false);
    const openAbout = () => { setAboutLoaded(true); setShowAbout(true); };
    const [showSettings, setShowSettings] = useState(false);
    const openSettings = () => setShowSettings(true);
    const [showFeedback, setShowFeedback] = useState(false);
    const openFeedback = () => setShowFeedback(true);

    // Browser fullscreen for the whole app (the tabletop especially). State follows the
    // browser's own fullscreenchange event, so leaving with Esc or F11 keeps the icon right.
    // The webkit forms cover Safari, which still doesn't ship the unprefixed API.
    const fullscreenElement = () => document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
    const fullscreenSupported = !!(document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen);
    const [isFullscreen, setIsFullscreen] = useState(() => !!fullscreenElement());
    useEffect(() => {
        const sync = () => setIsFullscreen(!!fullscreenElement());
        document.addEventListener('fullscreenchange', sync);
        document.addEventListener('webkitfullscreenchange', sync);
        return () => {
            document.removeEventListener('fullscreenchange', sync);
            document.removeEventListener('webkitfullscreenchange', sync);
        };
    }, []);
    function toggleFullscreen() {
        const el = document.documentElement;
        const request = fullscreenElement()
            ? (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document)
            : (el.requestFullscreen ?? el.webkitRequestFullscreen)?.call(el);
        // Browsers refuse (e.g. inside an iframe without allowfullscreen); nothing to undo.
        Promise.resolve(request).catch(() => {});
    }

    async function logout() {
        if (!(await confirmLogout())) return;
        announceLogout();
        sessionStorage.removeItem("user");
        clearSageHistory();
        navigate('/');
    }

    useEffect(() => {
        const headers = {'Authorization': 'Bearer ' + userJSON.accessToken};
        fetch(`${process.env.REACT_APP_API_PROXY}/profile/getProfileForUser/${userJSON.id}`, {headers})
            // A brand-new account may have no profile row yet, so a non-OK response is a normal
            // outcome rather than an error - calling res.json() on it would reject and leave the
            // avatar unset, which is what used to show new users nothing at all.
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                // Profile avatars live in S3 (dragondropvtt-avatars-public), served by
                // fileName — the avatar_image bytea column this used to decode is gone
                // (see backend drop_profile_avatar_image_column.sql). No upload → default.
                setAvatarImage(profileAvatarUrl(data?.fileName));
                setAvatarBorderColor(data?.avatarBorderColor);
            })
            .catch(() => setAvatarImage(DEFAULT_PROFILE_AVATAR));
    }, [showProfile]);

    useEffect(() => {
        const headers = {'Authorization': 'Bearer ' + userJSON.accessToken};
        fetch(`${process.env.REACT_APP_API_PROXY}/campaigns/getAllCampaignsForUser`, {headers})
            .then(res => res.json())
            .then(campaigns => {
                const selectedCampaignId = JSON.parse(localStorage.getItem("selectedCampaignId_" + userJSON.username) || "null");
                // Backend's `active` flag (User.activeCampaignId) is authoritative across devices;
                // the local cache is only a fallback for pre-migration users with nothing set server-side yet.
                const active = campaigns.find(c => c.active) ?? campaigns.find(c => c.id === selectedCampaignId) ?? campaigns[0];
                if (active) {
                    setCampaignName(active.campaignName);
                    const party = [active.party, ...(active.parties ?? [])].filter(Boolean)[0] ?? null;
                    if (party?.name) setPartyName(party.name);
                }
            })
            .catch(() => {});

        const handleCampaignChanged = (e) => {
            setCampaignName(e.detail.campaignName);
            if (e.detail.partyName !== undefined) setPartyName(e.detail.partyName);
            Swal.fire({
                toast: true, position: 'bottom-end', timer: 2500, showConfirmButton: false,
                icon: 'success', title: `${e.detail.campaignName} is now active`,
            });
        };
        window.addEventListener('activeCampaignChanged', handleCampaignChanged);
        return () => window.removeEventListener('activeCampaignChanged', handleCampaignChanged);
    }, []);

    return (
        <>
            <ProfileModal showProfile={showProfile} setShowProfile={setShowProfile}/>
            {aboutLoaded && (
                <Suspense fallback={null}>
                    <AboutModal show={showAbout} onHide={() => setShowAbout(false)}/>
                </Suspense>
            )}
            {helpLoaded && (
                <Suspense fallback={null}>
                    <HelpModal show={showHelp} onHide={() => setShowHelp(false)}/>
                </Suspense>
            )}
            <GlobalSettingsModal show={showSettings} onHide={() => setShowSettings(false)}/>
            {/* campaignName is already resolved here for the header strip, so the report
                carries the active campaign without a second lookup. */}
            <FeedbackModal show={showFeedback} onHide={() => setShowFeedback(false)} campaignName={campaignName}/>
            <Navbar fixed="top" bg={lightMode ? undefined : "dark"} data-bs-theme={lightMode ? "light" : "dark"}
                    style={{ height: '60px', borderBottom: `1px solid ${lightMode ? 'var(--th-7b5fb5)' : 'var(--th-2c2433)'}`, zIndex: 2147483647 }}>
                <Container fluid style={{ paddingLeft: '12px', paddingRight: '12px', gap: 0 }}>

                    {/* Left — logo + brand */}
                    <Navbar.Brand href="/tavern" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 0 }}>
                        <img alt="" src={DDLogo} width="36" height="36" className="d-inline-block align-middle brand-logo brand-logo--sm" />
                        {/* Wordmark: same lockup as the login screen (LoginForm.css .brand-title) —
                            mixed-case name beside a filled purple "VTT" chip. The chip keeps its own
                            ground, so it is identical in both themes; only the name flips, because
                            white would sit at about 1.9:1 on light mode's var(--th-a098c4) header. No text
                            shadow here — that exists on login only to hold the name off a photo. */}
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                            fontSize: '1.15rem',
                            fontWeight: 700,
                            letterSpacing: '-0.01em',
                            lineHeight: 1.15,
                            color: lightMode ? 'var(--th-1a1225)' : '#FFFFFF',
                        }}>
                            DropWyrm
                            {/* Asymmetric padding balances the trailing letter-space after the last T. */}
                            <span className="brand-vtt-chip" style={{
                                padding: '3px 5px 3px 7px',
                                borderRadius: '4px',
                                background: 'var(--th-6c4f9b)',
                                color: '#FFFFFF',
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                letterSpacing: '0.18em',
                                lineHeight: 1,
                            }}>
                                VTT
                            </span>
                        </span>
                    </Navbar.Brand>

                    {/* Center — active campaign + party */}
                    <div style={{
                        position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        lineHeight: 1.2, whiteSpace: 'nowrap',
                    }}>
                        <span style={{ fontSize: '0.85rem', color: 'var(--lm-tx-th-9c6fd6)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            {campaignName}
                        </span>
                        {partyName && (
                            <span style={{ fontSize: '0.7rem', color: 'var(--lm-tx-th-a89bc2)', letterSpacing: '0.04em' }}>
                                {partyName}
                            </span>
                        )}
                    </div>

                    {/* Right — avatar + dropdown + logout */}
                    <Nav className="ms-auto" style={{ alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="avatar" style={{
                                // Quoted so a key containing spaces still parses; omitted entirely
                                // when unset, since url(null) resolves to a bogus /null request.
                                backgroundImage: avatarImage ? `url("${avatarImage}")` : undefined,
                                borderColor: avatarBorderColor,
                                flexShrink: 0,
                            }} />
                            <NavDropdown title={userJSON.username} id="header-user-dropdown" align="end"
                                         onToggle={isOpen => { if (!isOpen) setCommunityOpen(false); }}>
                                {/* Community submenu. React-Bootstrap has no nested dropdowns, so this is
                                    a plain row (not a Dropdown.Item, which would close the menu on click)
                                    with its own flyout. Opens to the left: the menu is pinned to the
                                    right edge. Hover for mouse, tap for touch. */}
                                <div style={{ position: 'relative' }}
                                     onMouseEnter={() => setCommunityOpen(true)}
                                     onMouseLeave={() => setCommunityOpen(false)}>
                                    <button type="button" className="dropdown-item" aria-haspopup="true" aria-expanded={communityOpen}
                                            onClick={() => setCommunityOpen(o => !o)}
                                            style={{ display: 'flex', alignItems: 'center' }}>
                                        <ChevronLeft fontSize="small" sx={{ ml: -0.75, mr: 0.25 }} />
                                        <Forum fontSize="small" sx={{ mr: 1 }} /> Community
                                    </button>
                                    {communityOpen && (
                                        <div className="dropdown-menu show" style={{ position: 'absolute', top: -8, right: '100%', left: 'auto', margin: 0 }}>
                                            <NavDropdown.Item href="https://discord.gg/rPGeR6QW" target="_blank" rel="noopener noreferrer">
                                                <FaDiscord size={20} style={{ marginRight: 8 }} /> Discord
                                            </NavDropdown.Item>
                                            <NavDropdown.Item href="https://www.instagram.com/dropwyrm/" target="_blank" rel="noopener noreferrer">
                                                <FaInstagram size={20} style={{ marginRight: 8 }} /> Instagram
                                            </NavDropdown.Item>
                                        </div>
                                    )}
                                </div>
                                <NavDropdown.Divider />
                                <NavDropdown.Item onClick={handleShowProfile}>
                                    <ManageAccounts fontSize="small" sx={{ mr: 1 }} /> Profile
                                </NavDropdown.Item>
                                <NavDropdown.Item onClick={() => navigate('/billing')}>
                                    <CreditCard fontSize="small" sx={{ mr: 1 }} /> Plans &amp; Billing
                                </NavDropdown.Item>
                                {superAdmin && (
                                    <NavDropdown.Item onClick={() => navigate('/admin/users')}>
                                        <AdminPanelSettings fontSize="small" sx={{ mr: 1 }} /> User Administration
                                    </NavDropdown.Item>
                                )}
                                {superAdmin && (
                                    <NavDropdown.Item onClick={() => navigate('/admin/storage')}>
                                        <StorageIcon fontSize="small" sx={{ mr: 1 }} /> Storage Usage
                                    </NavDropdown.Item>
                                )}
                                <NavDropdown.Item onClick={openSettings}>
                                    <Settings fontSize="small" sx={{ mr: 1 }} /> Settings
                                </NavDropdown.Item>
                                <NavDropdown.Item onClick={openFeedback}>
                                    <BugReport fontSize="small" sx={{ mr: 1 }} /> Send Feedback
                                </NavDropdown.Item>
                                <NavDropdown.Item onClick={openHelp}>
                                    <QuestionMark fontSize="small" sx={{ mr: 1 }} /> Help
                                </NavDropdown.Item>
                                <NavDropdown.Item onClick={openAbout}>
                                    <Info fontSize="small" sx={{ mr: 1 }} /> About
                                </NavDropdown.Item>
                            </NavDropdown>
                            {fullscreenSupported && (
                                <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'} placement="bottom" disableHoverListener={!showTooltips}>
                                    <IconButton onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'} sx={{
                                        color: lightMode ? 'var(--th-4a3b66)' : '#F5F5F5',
                                    }}>
                                        {isFullscreen ? <FullscreenExit fontSize="small"/> : <Fullscreen fontSize="small"/>}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={helpModeOn ? 'Exit help mode' : 'Show help for this view'} placement="bottom" disableHoverListener={!showTooltips}>
                                <IconButton onClick={toggleHelp} sx={{
                                    color: helpModeOn ? 'var(--lm-tx-th-c9a0f0)' : (lightMode ? 'var(--th-4a3b66)' : '#F5F5F5'),
                                    backgroundColor: helpModeOn ? 'rgba(var(--th-6c4f9b-rgb), 0.25)' : 'transparent',
                                }}>
                                    <HelpOutline fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Logout" placement="bottom" disableHoverListener={!showTooltips}>
                                <IconButton onClick={logout} aria-label="Logout" sx={{
                                    color: lightMode ? 'var(--th-4a3b66)' : '#F5F5F5',
                                }}>
                                    <Logout fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </div>
                    </Nav>

                </Container>
            </Navbar>
        </>
    );
};

export default Header;